import { z } from 'zod';
/**
 * ehr#611 / ehr#612: a provider's payor enrollments (provider_payor_enrollments). The Provider
 * Profile's "Payor enrollments" section and Data & settings > Payors' "Users accepting this
 * insurance" read and write the same rows: a provider enrolled with a payor accepts it. The payor
 * itself is a row of RCM's list (rcm_payors), linked by its id.
 */
/** The Provider Profile's enrollment statuses, in its sort order. */
export const PAYOR_ENROLLMENT_STATUSES = [
    'In process',
    'Not started',
    'Panel closed',
    'Enrolled',
];
/** The id shape the payors API accepts for an RCM payor. */
export const PAYOR_ID_RE = /^[A-Za-z0-9-]{1,64}$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Create / change body from the Provider Profile. */
export const payorEnrollmentInputSchema = z.object({
    payorId: z.string().trim().regex(PAYOR_ID_RE, 'Pick the payor.'),
    status: z.enum(PAYOR_ENROLLMENT_STATUSES),
    effective: z
        .string()
        .trim()
        .refine((v) => v === '' || ISO_DATE_RE.test(v), { message: 'Use a yyyy-mm-dd date.' })
        .default(''),
    pid: z.string().trim().max(120).default(''),
    note: z.string().trim().max(500).default(''),
});
/** The enrollment pop-up's fields on the payor's side: the payor is the one open, the user fixed. */
const enrollmentFields = payorEnrollmentInputSchema.omit({ payorId: true });
/**
 * Add a user from the payor's side ("Add user"). ehr#611 (Chase 10-23): picking the user opens the
 * enrollment pop-up, so the add carries its fields; one sent without them is an Enrolled row.
 */
export const payorUserInputSchema = enrollmentFields
    .extend({ status: enrollmentFields.shape.status.default('Enrolled') })
    .extend({ userId: z.string().trim().min(1).max(64) });
/** ehr#611: change a user's enrollment from the payor's side (PUT /payors/:id/users/:userId). */
export const payorUserChangeSchema = enrollmentFields;
/** Two letters for an avatar: first and last word of the name ("Maya Chen" -> "MC"). */
export function initialsOf(first, last, fallback = '') {
    const f = first.trim();
    const l = last.trim();
    if (f || l)
        return ((f[0] ?? '') + (l[0] ?? '')).toUpperCase() || '?';
    const words = fallback
        .trim()
        .split(/[\s@._-]+/)
        .filter(Boolean);
    return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}
/** "Jordan Williams, LCSW": the name, then the credentials when the profile has them. */
export function userLabel(name, cred) {
    const n = name.trim();
    const c = cred.trim();
    return c && n ? `${n}, ${c}` : n || c;
}
/**
 * The label for each payor id: its name, plus " (ST)" when another payor in the practice has the
 * same name (RCM allows one name per state), so a picker can tell them apart.
 */
export function payorLabels(rows) {
    const count = new Map();
    for (const r of rows) {
        const k = r.name.trim().toLowerCase();
        count.set(k, (count.get(k) ?? 0) + 1);
    }
    const out = new Map();
    for (const r of rows) {
        const name = r.name.trim();
        const shared = (count.get(name.toLowerCase()) ?? 0) > 1;
        out.set(r.id, shared && r.state ? `${name} (${r.state})` : name);
    }
    return out;
}
/** Order on a payor's list: enrolled first, then by name. */
export function sortAcceptingUsers(rows) {
    return [...rows].sort((a, b) => Number(b.status === 'Enrolled') - Number(a.status === 'Enrolled') ||
        a.label.localeCompare(b.label));
}
//# sourceMappingURL=payorEnrollments.js.map